import React, { useEffect, useState } from 'react';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;

const AdminFinance = () => {
  const [ledger, setLedger] = useState({ items: [], admin_total: 0 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feePercent, setFeePercent] = useState('18');
  const [saving, setSaving] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('token') : null;

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [lres, eres, sres] = await Promise.all([
        fetch(`${API}/admin/ledger`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const l = lres.ok ? await lres.json() : { items: [], admin_total: 0 };
      const ev = eres.ok ? await eres.json() : [];
      const settings = sres.ok ? await sres.json() : [];
      setLedger(l);
      setEvents(Array.isArray(ev) ? ev : []);
      try {
        const fee = (settings || []).find(it => it.key === 'platform_fee_percent');
        if (fee) {
          const pct = parseFloat(fee.value) * 100;
          if (!Number.isNaN(pct)) setFeePercent(String(pct));
        }
      } catch {}
    } catch (e) {
      console.error(e);
      setLedger({ items: [], admin_total: 0 });
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveFee = async () => {
    try {
      setSaving(true);
      const pctFloat = Math.max(0, parseFloat(feePercent || '0')) / 100.0;
      const resp = await fetch(`${API}/admin/settings/platform_fee_percent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: String(pctFloat), description: 'Platform fee percent for employer (0-1)', category: 'payments' })
      });
      if (!resp.ok) throw new Error('failed to save');
      alert('手数料率を保存しました');
      fetchAll();
    } catch (e) {
      console.error(e);
      alert('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">運営 決済・手数料ダッシュボード</h1>
        <div className="text-sm text-gray-700">運営累計: <span className="font-semibold">¥{Number(ledger.admin_total).toFixed(2)}</span></div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b font-semibold flex items-center justify-between">
          <div>プラットフォーム手数料（事業者負担）</div>
          <div className="flex items-center gap-2">
            <input type="number" min="0" step="0.1" className="border rounded px-2 py-1 w-24 text-right" value={feePercent}
              onChange={e => setFeePercent(e.target.value)} />
            <span className="text-sm">%</span>
            <button onClick={saveFee} disabled={saving}
              className={`px-3 py-1 rounded text-white ${saving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>保存</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b font-semibold">運営台帳</div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm text-gray-500">日時</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">ロール</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">金額</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">方向</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">区分</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">説明</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">参照</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">読み込み中...</td></tr>
              ) : ledger.items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">記録がありません</td></tr>
              ) : (
                ledger.items.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="px-4 py-2 text-sm">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm">{e.role}</td>
                    <td className="px-4 py-2 text-sm">¥{Number(e.amount).toFixed(2)}</td>
                    <td className="px-4 py-2 text-sm">{e.direction}</td>
                    <td className="px-4 py-2 text-sm">{e.category}</td>
                    <td className="px-4 py-2 text-sm">{e.description || '-'}</td>
                    <td className="px-4 py-2 text-sm">{e.ref_type || '-'} / {e.ref_id || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b font-semibold">決済イベント</div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm text-gray-500">日時</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">タイプ</th>
                <th className="px-4 py-2 text-left text-sm text-gray-500">ID</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">イベントがありません</td></tr>
              ) : (
                events.map(ev => (
                  <tr key={ev.id} className="border-t">
                    <td className="px-4 py-2 text-sm">{new Date(ev.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm">{ev.type}</td>
                    <td className="px-4 py-2 text-sm">{ev.id}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminFinance;
